import React from 'react';
import { Box, Snackbar, Alert, useTheme } from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';
import { mrLogo } from '../../../assets/branding';
import { FormPaper, GoogleButton } from '../../theme/formComponents';
import { IFormContainerProps } from '../../types/formContainerProps';

const FormContainer = ({
  children,
  snackBarOpts,
  googleAuthConfig,
}: IFormContainerProps) => {
  const theme = useTheme();
  let authTabs = null;
  let otherChildren = React.Children.toArray(children);

  if (otherChildren.length > 0) {
    authTabs = otherChildren[0];
    otherChildren = otherChildren.slice(1);
  }

  const getAlertStyle = (isError: boolean) => {
    if (isError) {
      return {
        width: '100%',
        borderRadius: '8px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        fontWeight: 500,
        fontSize: '0.9rem',
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
        '& .MuiAlert-icon': {
          color: theme.palette.error.contrastText,
        },
      };
    } else if (snackBarOpts.warning) {
      return {
        width: '100%',
        borderRadius: '8px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        fontWeight: 500,
        fontSize: '0.9rem',
        backgroundColor: theme.palette.warning.main,
        color: theme.palette.warning.contrastText,
        '& .MuiAlert-icon': {
          color: theme.palette.warning.contrastText,
        },
      };
    } else {
      return {
        width: '100%',
        borderRadius: '8px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        fontWeight: 500,
        fontSize: '0.9rem',
        backgroundColor: theme.palette.success.main,
        color: theme.palette.success.contrastText,
        '& .MuiAlert-icon': {
          color: theme.palette.success.contrastText,
        },
      };
    }
  };

  const getAlertSeverity = () => {
    if (snackBarOpts.error) return 'error';
    if (snackBarOpts.warning) return 'warning';
    return 'success';
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: { xs: 'flex-start', sm: 'center' },
        width: '100%',
        height: { xs: 'auto', sm: '100%' },
        paddingTop: { xs: '0px', sm: 0 },
        paddingBottom: { xs: '15px', sm: 0 }
      }}
    >
      <FormPaper
        elevation={0}
        sx={{
          maxWidth: 550,
          width: '100%',
          padding: {
            xs: '0px 16px 0px 16px',
            sm: '0px 32px 0px 32px'
          },
          marginTop: {
            xs: '0px',
            sm: '-100px'
          },
          marginBottom: {
            xs: '20px',
            sm: '0px'
          },
          borderRadius: '0px',
          boxShadow: 'none',
          margin: { xs: '0px', sm: '0px !important' },
          overflowX: 'hidden',
          overflow: { xs: 'visible', sm: 'hidden' },
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Box sx={{
          textAlign: 'center',
          mb: { xs: 2, sm: 2 },
          mt: { xs: 3, sm: 0 },
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: { xs: 'auto', sm: 110 }
        }}>
          <img
            src={mrLogo}
            alt='logo'
            style={{
              width: 140,
              maxWidth: '100%'
            }}
            data-testid='login-form-header-icon'
          />
        </Box>

        <FormPaper
          elevation={0}
          sx={{
            width: '100%',
            padding: {
              xs: '12px !important',
              sm: '26px !important'
            },
            borderRadius: 2,
            border: '1px solid #e0e0e0',
            backgroundColor: 'white',
            mb: 3,
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: { xs: 'none', sm: '100vh' }
          }}
          style={{
            backgroundColor: theme.palette.background.paper,
          }}
        >
          {authTabs}

          {googleAuthConfig?.enabled && (
            <GoogleButton
              fullWidth
              variant='outlined'
              startIcon={<GoogleIcon />}
              onClick={googleAuthConfig.handleAuth}
              data-testid='google-login-btn'
              sx={{
                height: 38,
                border: '1px solid rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
                backgroundColor: '#fff',
                color: 'rgba(0, 0, 0, 0.87)',
                fontWeight: 500,
                fontSize: '0.95rem',
                mb: 3,
                mt: 3,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              {googleAuthConfig.btnText || 'Continue with Google'}
            </GoogleButton>
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              my: 2,
            }}
          >
          </Box>

          <Box sx={{ width: '100%' }}>
            {otherChildren}
          </Box>
        </FormPaper>
        {snackBarOpts.useSnackBar && (
          <Snackbar
            open={snackBarOpts.showSnackbar}
            autoHideDuration={snackBarOpts.duration || 6000}
            onClose={snackBarOpts.onCloseSnackbar}
            anchorOrigin={
              snackBarOpts.anchorOrigin || {
                vertical: 'top',
                horizontal: 'right',
              }
            }
            sx={{
              '& .MuiSnackbarContent-root': {
                minWidth: '300px',
                borderRadius: '8px',
              }
            }}
          >
            <Alert
              onClose={snackBarOpts.onCloseSnackbar}
              severity={getAlertSeverity()}
              variant="filled"
              sx={getAlertStyle(snackBarOpts.error)}
              {...(snackBarOpts.sx && { sx: { ...getAlertStyle(snackBarOpts.error), ...snackBarOpts.sx } })}
            >
              {snackBarOpts.message}
            </Alert>
          </Snackbar>
        )}
      </FormPaper>
    </Box>
  );
};

export default FormContainer;